import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Zap, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useGeo } from "@/lib/geo";

// All prize / tournament rows are India-only. Rest of the world sees a
// clean feature list with zero cash-prize wording.
const FEATURES_COMMON = [
  { label: "Play the Chrome Dino Game", free: true, pro: true },
  { label: "Compete on global daily leaderboard", free: true, pro: true },
  { label: "3 resumes per run", free: "with ad", pro: "no ads" },
  { label: "Create private friend rooms", free: "3/day (with ad)", pro: "Unlimited — no ads" },
  { label: "Join friend rooms", free: "Unlimited", pro: "Unlimited" },
  { label: "Zero ads (no rewarded ads, no interstitials)", free: false, pro: true },
  { label: "Golden VIP profile card with animated gold border", free: false, pro: true },
  { label: "Shimmering premium name across every leaderboard & room", free: false, pro: true },
  { label: "Rotating gold avatar ring + crown badge on all rankings", free: false, pro: true },
  { label: "Full career stats visible on your profile (All-Time High, Room Wins, #1 Finishes)", free: false, pro: true },
  { label: "See full stats of every other player", free: false, pro: true },
  { label: "Priority room slots at peak hours", free: false, pro: true },
  { label: "Early access to new game modes", free: false, pro: true },
];
const FEATURES_IN_EXTRA = [
  { label: "Exclusive DarePass tournaments — bigger prize pools", free: false, pro: true },
  { label: "DarePass-only weekly cash prize (members compete separately)", free: false, pro: true },
];
function buildFeatures(isIndia) {
  return isIndia
    ? [...FEATURES_COMMON.slice(0, 11), ...FEATURES_IN_EXTRA, ...FEATURES_COMMON.slice(11)]
    : FEATURES_COMMON;
}

// Loads the Razorpay Checkout script exactly once.
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Pricing() {
  const navigate = useNavigate();
  const geo = useGeo();
  const isIndia = geo.isIndia;
  const [me, setMe] = useState(null);
  const [config, setConfig] = useState(null);
  const [processing, setProcessing] = useState(false);
  // International-only toggle. India users force "monthly" regardless.
  const [planVariant, setPlanVariant] = useState("monthly");

  useEffect(() => {
    (async () => {
      try {
        const [m, c] = await Promise.all([
          api.get("/auth/me").catch(() => null),
          api.get(
            `/subscription/darepass/config?country=${geo.country || "IN"}&plan_variant=${
              isIndia ? "monthly" : planVariant
            }`
          ),
        ]);
        if (m) setMe(m.data);
        setConfig(c.data);
      } catch {}
    })();
  }, [geo.country, planVariant, isIndia]);

  const isPro = !!me?.is_pro;
  const isLifetime = !!me?.is_lifetime;
  const priceInr = config?.price_inr ?? 99;
  const priceUsdMonthly = config?.price_usd ?? Number(process.env.REACT_APP_DAREPASS_PRICE_USD || "2.99");
  const priceUsdLifetime = config?.price_usd_lifetime ?? 29.99;
  const lifetimeAvailable = !isIndia; // backend also confirms via config.lifetime_available
  const activeVariant = isIndia ? "monthly" : planVariant;
  // Resolved by the server-side geo logic; falls back to the local guess.
  const resolvedPrice =
    config?.price ??
    (isIndia
      ? priceInr
      : activeVariant === "lifetime"
      ? priceUsdLifetime
      : priceUsdMonthly);
  const displayPrice = isIndia ? `₹${resolvedPrice}` : `$${resolvedPrice}`;
  const priceSuffix = isIndia
    ? "/month"
    : activeVariant === "lifetime"
    ? " one-time"
    : "/month";
  const ctaLabel = isIndia
    ? `Pay ${displayPrice} & Activate`
    : activeVariant === "lifetime"
    ? `Pay ${displayPrice} & Activate Lifetime`
    : `Pay ${displayPrice} & Activate`;
  const currencyCode = isIndia ? "INR" : "USD";
  const freePrice = isIndia ? "₹0" : "$0";
  const features = buildFeatures(isIndia);

  const handleUpgrade = async () => {
    if (!me) {
      toast.error("Please sign in first");
      navigate("/");
      return;
    }
    if (!config?.enabled) {
      toast.error("Payments not configured yet. Try again later.");
      return;
    }
    setProcessing(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error("Failed to load payment SDK");
        setProcessing(false);
        return;
      }
      const orderRes = await api.post("/subscription/darepass/order", {
        country: geo.country || "IN",
        plan_variant: isIndia ? "monthly" : planVariant,
      });
      const { order_id, amount, currency, key_id } = orderRes.data;

      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency,
        name: "DareDino",
        description: isIndia
          ? `DarePass — ₹${resolvedPrice}/month`
          : activeVariant === "lifetime"
          ? `DarePass Lifetime — $${resolvedPrice} one-time`
          : `DarePass — $${resolvedPrice}/month`,
        order_id,
        prefill: {
          name: me.name || "",
          email: me.email || "",
        },
        theme: { color: "#EAEB18" },
        handler: async (resp) => {
          try {
            await api.post("/subscription/darepass/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("DarePass activated! Ads disabled.", {
              style: { background: "#0B0C10", border: "1px solid #EAEB18", color: "#EAEB18" },
            });
            // Refresh /auth/me so is_pro flag updates app-wide
            const refreshed = await api.get("/auth/me").catch(() => null);
            if (refreshed) setMe(refreshed.data);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => setProcessing(false),
        },
      });
      rzp.on("payment.failed", (r) => {
        toast.error(r?.error?.description || "Payment failed");
        setProcessing(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setProcessing(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#050505]">
      <div className="absolute inset-0 grid-overlay opacity-30 pointer-events-none" />

      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 border-b border-[#00F0FF]/20">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#94A3B8] hover:text-white text-xs uppercase tracking-widest"
          data-testid="pricing-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="font-heading font-black text-lg text-white tracking-wider">
          DARE<span className="text-[#EAEB18]">DINO</span> <span className="text-[#00F0FF]">/ PRICING</span>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="text-center mb-12">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] mb-3">[ MEMBERSHIP PLANS ]</div>
          <h1 className="font-heading font-black text-3xl md:text-5xl text-white mb-3">
            Get <span className="text-[#EAEB18]">DarePass</span>. Play <span className="text-[#00F0FF]">Ad-Free</span>.
          </h1>
          <p className="text-sm text-[#94A3B8] max-w-2xl mx-auto">
            {isIndia
              ? "Free forever gets you the daily global prize game. DarePass unlocks a members-only tournament, unlimited rooms, and zero ads across the site."
              : "Free forever gets you the game and the live global leaderboard. DarePass unlocks unlimited private rooms, a premium profile frame, and zero ads across the site."}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {/* FREE */}
          <div className="border border-[#00F0FF]/40 bg-[#0B0C10] p-8 flex flex-col">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-3">Free</div>
            <div className="mb-6">
              <span className="font-heading font-black text-5xl text-white" data-testid="free-price">{freePrice}</span>
              <span className="text-sm text-[#94A3B8]"> /forever</span>
            </div>
            <button
              disabled
              data-testid="pricing-free-btn"
              className="w-full py-3 border border-[#00F0FF]/40 text-[#94A3B8] text-xs uppercase tracking-widest mb-6 cursor-default"
            >
              {isPro ? "Basic Plan" : "Current Plan"}
            </button>
            <ul className="space-y-3 flex-1">
              {features.map((f) => (
                <li key={f.label} className="flex items-start gap-2 text-xs text-[#E2E8F0]">
                  {f.free ? (
                    <Check size={16} className="text-[#00F0FF] shrink-0 mt-0.5" />
                  ) : (
                    <X size={16} className="text-[#FF007F] shrink-0 mt-0.5" />
                  )}
                  <span>
                    {f.label}
                    {typeof f.free === "string" && <span className="text-[#94A3B8]"> — {f.free}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* PRO */}
          <div className="relative border-2 border-[#EAEB18] bg-[#0B0C10] p-8 flex flex-col">
            <div className="absolute -top-3 left-6 bg-[#EAEB18] text-black text-[10px] uppercase tracking-widest font-black px-3 py-1 flex items-center gap-1" data-testid="pro-ribbon">
              <Sparkles size={12} /> {isIndia ? "Members-only prizes" : (activeVariant === "lifetime" ? "Best value · One-time" : "Zero ads · Premium frame")}
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#EAEB18] mb-3">DarePass</div>

            {/* Monthly / Lifetime toggle — international only */}
            {lifetimeAvailable && !isPro && (
              <div
                data-testid="plan-variant-toggle"
                role="tablist"
                aria-label="DarePass plan variant"
                className="grid grid-cols-2 gap-0 mb-4 border-2 border-[#EAEB18]/40 bg-black/60"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeVariant === "monthly"}
                  data-testid="plan-toggle-monthly"
                  onClick={() => setPlanVariant("monthly")}
                  className={`relative py-2 text-[10px] uppercase tracking-[0.2em] font-heading font-black transition-colors ${
                    activeVariant === "monthly"
                      ? "bg-[#EAEB18] text-black"
                      : "text-[#94A3B8] hover:text-[#EAEB18]"
                  }`}
                >
                  Monthly <span className="opacity-70 tracking-wider">(${priceUsdMonthly}/mo)</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeVariant === "lifetime"}
                  data-testid="plan-toggle-lifetime"
                  onClick={() => setPlanVariant("lifetime")}
                  className={`relative py-2 text-[10px] uppercase tracking-[0.2em] font-heading font-black transition-colors flex items-center justify-center gap-1 ${
                    activeVariant === "lifetime"
                      ? "bg-[#EAEB18] text-black"
                      : "text-[#94A3B8] hover:text-[#EAEB18]"
                  }`}
                >
                  Lifetime <span className="opacity-70 tracking-wider">(${priceUsdLifetime})</span>
                  <span
                    data-testid="lifetime-best-value-badge"
                    className={`ml-1 text-[8px] uppercase tracking-widest font-black px-1.5 py-0.5 border ${
                      activeVariant === "lifetime"
                        ? "border-black/40 bg-black/10 text-black"
                        : "border-[#FF007F]/60 bg-[#FF007F]/10 text-[#FF007F]"
                    }`}
                  >
                    Best 🔥
                  </span>
                </button>
              </div>
            )}

            <div className="mb-1">
              <span className="font-heading font-black text-5xl text-white" data-testid="darepass-price">{displayPrice}</span>
              <span className="text-sm text-[#94A3B8]" data-testid="darepass-price-suffix">{priceSuffix}</span>
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#94A3B8]" data-testid="darepass-currency">{currencyCode}</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] mb-4" data-testid="darepass-validity-line">
              {isIndia
                ? "1-month validity · No auto-renew · Renew manually anytime"
                : activeVariant === "lifetime"
                ? "One-time payment · Perpetual DarePass access · No renewals ever"
                : "1-month validity · No auto-renew · Renew manually anytime"}
            </div>
            <div className="border border-[#EAEB18]/30 bg-[#EAEB18]/5 px-3 py-2 mb-4">
              <div className="text-[9px] uppercase tracking-widest text-[#EAEB18] mb-1">DarePass advantage</div>
              <p className="text-[11px] text-[#E2E8F0] leading-snug" data-testid="darepass-advantage">
                {isIndia
                  ? (<>Zero ads across the site, unlimited rooms with no ad-wall, 3 resumes per run without watching ads, a unique <strong className="text-[#EAEB18]">premium profile frame + shimmering name</strong> everyone can see, and access to a <strong className="text-[#EAEB18]">separate members-only weekly tournament</strong> with a dedicated cash prize pool.</>)
                  : activeVariant === "lifetime"
                  ? (<>Pay <strong className="text-[#EAEB18]">once</strong>, keep DarePass <strong className="text-[#EAEB18]">forever</strong>. Zero ads, unlimited rooms, premium profile frame + shimmering name across every leaderboard, priority slots at peak hours, early access to new modes — locked in for life. Break-even vs monthly in just 10 months.</>)
                  : (<>Zero ads across the site, unlimited rooms with no ad-wall, 3 resumes per run without watching ads, and a unique <strong className="text-[#EAEB18]">premium profile frame + shimmering name</strong> that everyone can see on every leaderboard and inside every private room.</>)}
              </p>
            </div>
            {isPro ? (
              <button
                disabled
                data-testid="pricing-active-btn"
                className="w-full py-3 bg-[#EAEB18] text-black text-xs uppercase tracking-widest font-bold mb-2 flex items-center justify-center gap-2 cursor-default"
              >
                <Sparkles size={14} /> {isLifetime ? "DarePass Lifetime · Active" : "DarePass Active"}
              </button>
            ) : (
              <button
                onClick={handleUpgrade}
                disabled={processing || !config?.enabled}
                data-testid="pricing-pro-btn"
                className="w-full py-3 bg-[#EAEB18]/20 border-2 border-[#EAEB18] text-[#EAEB18] text-xs uppercase tracking-widest font-bold mb-2 hover:bg-[#EAEB18] hover:text-black transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Zap size={14} /> {processing ? "Processing..." : ctaLabel}
              </button>
            )}
            {isPro && !isLifetime && me?.darepass_expires_at && (
              <p className="text-[10px] text-center text-[#94A3B8] uppercase tracking-widest mb-6">
                Expires on {new Date(me.darepass_expires_at).toLocaleDateString()} · Renew any time before it lapses
              </p>
            )}
            {isPro && isLifetime && (
              <p className="text-[10px] text-center text-[#EAEB18] uppercase tracking-widest mb-6" data-testid="lifetime-active-note">
                Lifetime access active · No expiry
              </p>
            )}
            {!isPro && (
              <p className="text-[10px] text-center text-[#94A3B8] uppercase tracking-widest mb-6" data-testid="checkout-methods">
                {isIndia
                  ? "Secure checkout via Razorpay · UPI · Cards · Netbanking"
                  : "Secure checkout via Razorpay · International Cards · Apple Pay"}
              </p>
            )}
            <ul className="space-y-3 flex-1">
              {features.map((f) => (
                <li key={f.label} className="flex items-start gap-2 text-xs text-[#E2E8F0]">
                  {f.pro ? (
                    <Check size={16} className="text-[#EAEB18] shrink-0 mt-0.5" />
                  ) : (
                    <X size={16} className="text-[#FF007F] shrink-0 mt-0.5" />
                  )}
                  <span>
                    {f.label}
                    {typeof f.pro === "string" && <span className="text-[#94A3B8]"> — {f.pro}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] text-center">
          One-time monthly pass · Not recurring · Renew whenever you like. By purchasing, you agree to our{" "}
          <button onClick={() => navigate("/terms-of-service")} className="text-[#00F0FF] hover:underline" data-testid="pricing-terms-link">Terms of Service</button>{" "}
          and{" "}
          <button onClick={() => navigate("/privacy-policy")} className="text-[#00F0FF] hover:underline" data-testid="pricing-privacy-link">Privacy Policy</button>.
        </div>
      </main>
    </div>
  );
}
